import React, { useContext, useMemo } from 'react';

import GraphemeSplitter from 'grapheme-splitter';
import { ThemeContext } from 'styled-components';

import { randomValueBetween } from '@pkg/utils';

import { Character, TextRevealWrapper, Word } from './styles';

interface Props {
    className?: string;
    text: string;
    /**
     * Add state if you want to control when text reveals. Or, ignore if you want
     * to text to reveal on load / using `<AnimatePresence />`.
     */
    state?: 'initial' | 'animate' | 'exit';
    preventOverflow?: boolean;
}

const YatTextReveal = ({ className, text, state, preventOverflow }: Props) => {
    const theme = useContext(ThemeContext);

    const words = useMemo(() => text.split(' '), [text]);

    const wordsSplitIntoCharacters: string[][] = useMemo(() => {
        const splitter = new GraphemeSplitter();
        const wordsSplitIntoCharacters: string[][] = [];

        // Split words into characters
        words.map((word) => wordsSplitIntoCharacters.push(splitter.splitGraphemes(word)));

        // Add space after every word if not last
        wordsSplitIntoCharacters.forEach((_, x) => {
            const lastWord = x === wordsSplitIntoCharacters.length - 1;
            if (!lastWord) wordsSplitIntoCharacters[x].push(' ');
        });

        return wordsSplitIntoCharacters;
    }, [words]);

    return (
        <TextRevealWrapper
            className={className}
            aria-label={text}
            initial="initial"
            animate={state ? (state === 'initial' ? undefined : state) : 'animate'}
            exit={state ? undefined : 'exit'}
            $preventOverflow={preventOverflow}
        >
            {wordsSplitIntoCharacters.map((word, i) => (
                <Word key={i}>
                    {word.map((character, x) => {
                        const delay = randomValueBetween({
                            min: 0,
                            max: theme.transitions.speeds.fast,
                        });

                        return (
                            <Character
                                key={`${i}-${x}`}
                                variants={{
                                    initial: { opacity: 0 },
                                    animate: {
                                        opacity: 1,
                                        transition: { delay, duration: theme.transitions.speeds.fast },
                                    },
                                    exit: {
                                        opacity: 0,
                                        transition: { delay, duration: theme.transitions.speeds.rapid },
                                    },
                                }}
                                aria-hidden
                            >
                                {character}
                            </Character>
                        );
                    })}
                </Word>
            ))}
        </TextRevealWrapper>
    );
};

export default YatTextReveal;
